import { useState, useEffect } from "react";
import { II } from "../../components/common/Icons";
import { usePMData } from "../../context/PMContext";
import PMModal from "./PMModal";
import api from "../../services/api";import "../../assets/styles/PM/PMProjectsPg.css";

function PMProjectsPg() {
  const {
    pmProjects,
    pmTeamMembers,
    loading,
    refreshPMData,
    createProject,
    addProjectMember,
    addProjectMembersBulk,
    removeProjectMember,
    updateMemberProgress,
    deleteProject,
    getAvailableMembers
  } = usePMData();

  // ✅ HR List State - FIXED
  const [hrList, setHrList] = useState([]);
  const [fetchingHR, setFetchingHR] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [availableMembers, setAvailableMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [projectForm, setProjectForm] = useState({
    name: "",
    description: "",
    client: "",
    status: "Planning",
    startDate: "",
    endDate: "",
    hrId: "",
    memberIds: []
  });

  // Multi-member form: selectedMembers = [{id, name, emp_id, role, hrRole}, ...]
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [memberRoles, setMemberRoles] = useState({}); // { userId: roleString }
  const [memberSearch, setMemberSearch] = useState("");
  // Keep old memberForm for backward compat (not used in new modal)
  const [memberForm, setMemberForm] = useState({ userId: "", role: "Member" });

  const [progressForm, setProgressForm] = useState({
    progress: 0,
    status: "On Track"
  });

  // ✅ FETCH ALL HR USERS - THIS IS THE FIX!
  useEffect(() => {
    if (showCreateModal) {
      fetchAllHRUsers();
    }
  }, [showCreateModal]);

  // Fetch HR users from dedicated PM endpoint
  const fetchAllHRUsers = async () => {
    setFetchingHR(true);
    try {
      const response = await fetch('/api/pm/hr-users', {
        headers: { 'Authorization': `Bearer ${api.getToken()}` }
      });
      if (response.ok) {
        const hrUsers = await response.json();
        setHrList(Array.isArray(hrUsers) ? hrUsers : []);
      } else {
        setHrList([]);
      }
    } catch (error) {
      console.error('Error fetching HR list:', error);
      setHrList([]);
    } finally {
      setFetchingHR(false);
    }
  };

  useEffect(() => {
    if (showMemberModal && selectedProject) {
      loadAvailableMembers(selectedProject.pid);
    }
  }, [showMemberModal, selectedProject]);

  const loadAvailableMembers = async (projectId) => {
    setLoadingMembers(true);
    try {
      const members = await getAvailableMembers(projectId);
      setAvailableMembers(members || []);
    } catch (error) {
      console.error('Error loading available members:', error);
      setAvailableMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const resetProjectForm = () => {
    setProjectForm({
      name: "",
      description: "",
      client: "",
      status: "Planning",
      startDate: "",
      endDate: "",
      hrId: "",
      memberIds: []
    });
  };

  const resetMemberForm = () => {
    setMemberForm({ userId: "", role: "Member" });
    setSelectedMemberIds([]);
    setMemberRoles({});
    setMemberSearch("");
  };

  const handleCreateProject = async () => {
    const isIsoDate = (v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(String(v));
    if (!projectForm.name) {
      alert("Project name is required");
      return;
    }
    if (!isIsoDate(projectForm.startDate) || !isIsoDate(projectForm.endDate)) {
      alert("Invalid date format. Use calendar picker (YYYY-MM-DD).");
      return;
    }
    if (projectForm.startDate && projectForm.endDate && projectForm.endDate < projectForm.startDate) {
      alert("End date must be after start date.");
      return;
    }

    try {
      await createProject(projectForm);
      setSuccessMsg(`Project "${projectForm.name}" created successfully!`);
      setShowCreateModal(false);
      resetProjectForm();
      await refreshPMData();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (error) {
      alert("Error creating project: " + error.message);
    }
  };

  const handleAddMember = async () => {
    if (selectedMemberIds.length === 0) {
      alert("Please select at least one member");
      return;
    }
    try {
      const members = selectedMemberIds.map(uid => ({
        userId: uid,
        role: memberRoles[uid] || "Member"
      }));
      // Use bulk endpoint if multiple, single if one
      if (members.length === 1) {
        await addProjectMember(selectedProject.pid, members[0].userId, members[0].role);
      } else {
        await addProjectMembersBulk(selectedProject.pid, members);
      }
      const count = selectedMemberIds.length;
      setSuccessMsg(`${count} member${count > 1 ? "s" : ""} added to ${selectedProject.name}`);
      setShowMemberModal(false);
      resetMemberForm();
      await refreshPMData();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (error) {
      alert("Error adding member: " + error.message);
    }
  };

  const handleRemoveMember = async (projectId, userId, memberName) => {
    if (!userId || userId === 'undefined') {
      alert("Error: Invalid member ID");
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${memberName} from this project?`)) {
      return;
    }

    try {
      await removeProjectMember(projectId, userId);
      setSuccessMsg(`${memberName} removed from project`);
      await refreshPMData();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (error) {
      alert("Error removing member: " + error.message);
    }
  };

  const handleUpdateProgress = async () => {
    if (!selectedMember) return;

    const userId = selectedMember.user_id || selectedMember.userId || selectedMember.id;

    if (!userId || userId === 'undefined') {
      alert("Error: Member ID not found");
      return;
    }

    try {
      await updateMemberProgress(
        selectedMember.project,
        userId,
        progressForm.progress,
        progressForm.status
      );
      setSuccessMsg(`Progress updated for ${selectedMember.name}`);
      setShowProgressModal(false);
      setSelectedMember(null);
      await refreshPMData();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (error) {
      alert("Error updating progress: " + error.message);
    }
  };

  const handleDeleteProject = async () => {
    if (!selectedProject) return;

    if (!window.confirm(`Are you sure you want to delete "${selectedProject.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteProject(selectedProject.pid);
      setSuccessMsg(`Project "${selectedProject.name}" deleted successfully`);
      setShowDeleteModal(false);
      setSelectedProject(null);
      await refreshPMData();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (error) {
      alert("Error deleting project: " + error.message);
    }
  };

  const filteredProjects = pmProjects?.filter((p) =>
  p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  p.pid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  p.client?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="pmprojectspg-inline-1">
        <div className="ob-pending-dot pmprojectspg-inline-2"></div>
        <div className="pmprojectspg-inline-3">Loading projects...</div>
      </div>);

  }

  return (
    <div>
      <div className="pmprojectspg-inline-4">




        
        <div className="sec pmprojectspg-inline-5">Projects & Team Overview</div>
        <div className="pmprojectspg-inline-6">
          <div className="pmprojectspg-inline-7">







            
            {II("search", 16, { c: "#94a3b8" })}
            <input








              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="pmprojectspg-inline-8" />
            
          </div>
          <button className="btn" onClick={() => setShowCreateModal(true)}>
            {II("plus", 14)} Create Project
          </button>
        </div>
      </div>

      {successMsg &&
      <div className="toast pmprojectspg-inline-9">
          {II("chk", 16, { c: "#2563eb" })} {successMsg}
        </div>
      }

      {filteredProjects.length === 0 ?
      <div className="cd pmprojectspg-inline-10">
          <div className="pmprojectspg-inline-11">📊</div>
          <div className="pmprojectspg-inline-12">
            {searchTerm ? 'No Projects Found' : 'No Projects Yet'}
          </div>
          <div className="pmprojectspg-inline-13">
            {searchTerm ?
          `No projects match "${searchTerm}"` :
          'Create your first project to get started.'
          }
          </div>
          {!searchTerm &&
        <button className="btn" onClick={() => setShowCreateModal(true)}>
              {II("plus", 14)} Create Your First Project
            </button>
        }
        </div> :

      <div className="pmprojectspg-inline-14">
          {filteredProjects.map((p) => {
          const projectMembers = pmTeamMembers?.filter((m) => m.project === p.pid) || [];

          return (
            <div key={p.pid} className="cd">
                <div className="cd-h">
                  <div className="pmprojectspg-inline-15">
                    <div className="pmprojectspg-inline-16">
                      <div className="cd-t">{p.name}</div>
                      <span className="chip chip-ip pmprojectspg-inline-17">{p.status}</span>
                    </div>
                    <div className="cd-s">
                      <span className="pmprojectspg-inline-18">{p.pid}</span> · {p.client || 'Internal'} · 
                      {p.start ? ` ${p.start}` : ' TBD'} → {p.deadline || 'TBD'}
                    </div>
                    {p.description &&
                  <div className="pmprojectspg-inline-19">
                        {p.description}
                      </div>
                  }
                  </div>
                  <div className="pmprojectspg-inline-20">
                    <div>
                      <div className="pmprojectspg-inline-21">Progress</div>
                      <div className="pmprojectspg-inline-22">
                        <div className="pmprojectspg-inline-23">
                          <div style={{ "--pmprojectspg-1-width":


                          p.progress + "%", "--pmprojectspg-1-background":
                          p.progress >= 70 ? "#2563eb" : p.progress >= 40 ? "#d97706" : "#ef4444" }} className="pmprojectspg-dyn-1" />

                        
                        
                        </div>
                        <span className="pmprojectspg-inline-24">
                          {p.progress}%
                        </span>
                      </div>
                    </div>
                    <button
                    className="btn-o btn-s pmprojectspg-inline-25"






                    onClick={() => {
                      setSelectedProject(p);
                      setShowDeleteModal(true);
                    }}
                    title="Delete Project">
                    
                      {II("trash", 14)} Delete
                    </button>
                  </div>
                </div>

                {p.hr_name &&
              <div className="pmprojectspg-inline-26">








                
                    {II("user", 14, { c: "#4338ca" })}
                    <span className="pmprojectspg-inline-27">HR Partner:</span> {p.hr_name}
                  </div>
              }

                <div className="cd-b pmprojectspg-inline-28">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Progress</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectMembers.length === 0 ?
                    <tr>
                          <td colSpan="6" className="pmprojectspg-inline-29">
                            No team members assigned. Add members to this project.
                          </td>
                        </tr> :

                    projectMembers.map((m) => {
                      const userId = m.user_id || m.userId || m.id;
                      const empId = m.emp_id || m.id;

                      return (
                        <tr key={userId || empId}>
                              <td className="pmprojectspg-inline-30">{empId}</td>
                              <td className="pmprojectspg-inline-31">{m.name}</td>
                              <td>{m.project_role || m.role || 'Member'}</td>
                              <td>
                                <div className="pmprojectspg-inline-32">
                                  <div className="pmprojectspg-inline-33">
                                    <div style={{ "--pmprojectspg-2-width":


                                  m.progress + "%", "--pmprojectspg-2-background":
                                  m.progress >= 70 ? "#2563eb" : m.progress >= 40 ? "#d97706" : "#ef4444" }} className="pmprojectspg-dyn-2" />

                                
                                
                                  </div>
                                  <span className="pmprojectspg-inline-34">{m.progress}%</span>
                                </div>
                              </td>
                              <td>
                                <span
                              className="chip pmprojectspg-dyn-3" style={{ "--pmprojectspg-3-background":

                                m.status === "On Track" ? "#eff6ff" : m.status === "Needs Attention" ? "#fff7ed" : "#fef2f2", "--pmprojectspg-3-color":
                                m.status === "On Track" ? "#2563eb" : m.status === "Needs Attention" ? "#d97706" : "#ef4444", "--pmprojectspg-3-border-color":
                                m.status === "On Track" ? "#bfdbfe" : m.status === "Needs Attention" ? "#fed7aa" : "#fecaca" }}>
                              
                              
                                  {m.status}
                                </span>
                              </td>
                              <td>
                                <div className="pmprojectspg-inline-35">
                                  <button
                                className="btn-o btn-s pmprojectspg-inline-36"

                                onClick={() => {
                                  setSelectedMember({
                                    ...m,
                                    userId: userId
                                  });
                                  setProgressForm({
                                    progress: m.progress,
                                    status: m.status
                                  });
                                  setShowProgressModal(true);
                                }}>
                                
                                    {II("edit", 12)} Progress
                                  </button>
                                  <button
                                className="btn-o btn-s pmprojectspg-inline-37"






                                onClick={() => handleRemoveMember(p.pid, userId, m.name)}>
                                
                                    {II("x", 12)} Remove
                                  </button>
                                </div>
                              </td>
                            </tr>);

                    })
                    }
                    </tbody>
                  </table>
                </div>

                <div className="pmprojectspg-inline-38">




                
                  <button
                  className="btn pmprojectspg-inline-39"

                  onClick={() => {
                    setSelectedProject(p);
                    setShowMemberModal(true);
                  }}>
                  
                    {II("plus", 12)} Add Member
                  </button>
                </div>
              </div>);

        })}
        </div>
      }

      {/* ========== CREATE PROJECT MODAL - ✅ FIXED HR DROPDOWN ========== */}
      <PMModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetProjectForm();
        }}
        title="Create New Project"
        wide={true}>
        
        <div>
          <div className="fg">
            <label className="fl">Project Name *</label>
            <input
              className="fi"
              value={projectForm.name}
              onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
              placeholder="e.g. Nexus Platform v3" />
            
          </div>

          <div className="fg">
            <label className="fl">Description</label>
            <textarea
              className="fta"
              rows={3}
              value={projectForm.description}
              onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
              placeholder="Project overview, goals, etc." />
            
          </div>

          <div className="fr2">
            <div className="fg">
              <label className="fl">Client</label>
              <input
                className="fi"
                value={projectForm.client}
                onChange={(e) => setProjectForm({ ...projectForm, client: e.target.value })}
                placeholder="Internal / Client Name" />
              
            </div>
            <div className="fg">
              <label className="fl">Status</label>
              <select
                className="fsel"
                value={projectForm.status}
                onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}>
                
                <option value="Planning">Planning</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="fr2">
            <div className="fg">
              <label className="fl">Start Date</label>
              <input
                className="fi"
                type="date"
                value={projectForm.startDate}
                onChange={(e) => setProjectForm({ ...projectForm, startDate: String(e.target.value || '').slice(0, 10) })} />
              
            </div>
            <div className="fg">
              <label className="fl">End Date</label>
              <input
                className="fi"
                type="date"
                value={projectForm.endDate}
                onChange={(e) => setProjectForm({ ...projectForm, endDate: String(e.target.value || '').slice(0, 10) })} />
              
            </div>
          </div>

          {/* ✅ FIXED: HR Partner Dropdown with ALL 3 HR USERS */}
          <div className="fg">
            <label className="fl">Assign HR Partner</label>
            {fetchingHR ?
            <div className="pmprojectspg-inline-40">
                <div className="ob-pending-dot pmprojectspg-inline-41"></div>
                Loading HR partners...
              </div> :

            <select
              className="fsel pmprojectspg-inline-42"
              value={projectForm.hrId}
              onChange={(e) => setProjectForm({ ...projectForm, hrId: e.target.value })}>

              
                <option value="">Select HR Partner (Optional)</option>
                {hrList.map((hr) => {
                const hrId = hr.id || hr.userId;
                const hrEmpId = hr.emp_id || hr.empId;
                const hrName = hr.name;

                return (
                  <option key={hrId} value={hrId}>
                      {hrName} ({hrEmpId}) - {hr.role || 'HR'}
                    </option>);

              })}
              </select>
            }
            {hrList.length === 0 && !fetchingHR &&
            <div className="pmprojectspg-inline-43">







              
                ⚠️ No HR partners found. Please add HR users first.
              </div>
            }
            
            {/* ✅ Debug info - remove after fixing */}
            <div className="pmprojectspg-inline-44">
              Found {hrList.length} HR users
            </div>
          </div>

          <div className="pmprojectspg-inline-45">
            <button className="btn-o" onClick={() => {
              setShowCreateModal(false);
              resetProjectForm();
            }}>Cancel</button>
            <button className="btn" onClick={handleCreateProject}>
              {II("chk", 14)} Create Project
            </button>
          </div>
        </div>
      </PMModal>

      {/* ========== ADD MEMBER MODAL — Multi-select ========== */}
      <PMModal
        open={showMemberModal}
        onClose={() => {
          setShowMemberModal(false);
          setSelectedProject(null);
          resetMemberForm();
          setAvailableMembers([]);
        }}
        title={`Add Members to ${selectedProject?.name || 'Project'}`}>

        {selectedProject && (
          <div>
            {/* Project info */}
            <div className="pmprojectspg-inline-46">
              <div className="pmprojectspg-inline-47">{selectedProject.name}</div>
              <div className="pmprojectspg-inline-48">{selectedProject.pid}</div>
            </div>

            {/* Search filter */}
            <div className="fg" style={{ marginBottom: 8 }}>
              <label className="fl">Search & Select Members *</label>
              <input
                className="fi"
                placeholder="Search by name or employee ID..."
                value={memberSearch}
                onChange={e => setMemberSearch(e.target.value)}
                style={{ marginBottom: 8 }}
              />
            </div>

            {/* Member list with checkboxes */}
            {loadingMembers ? (
              <div className="pmprojectspg-inline-49">
                <div className="ob-pending-dot pmprojectspg-inline-50"></div>
                Loading available members...
              </div>
            ) : (
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, maxHeight: 280, overflowY: "auto", marginBottom: 12 }}>
                {availableMembers.length === 0 ? (
                  <div style={{ padding: "20px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                    No available members to add
                  </div>
                ) : (
                  <>
                    {/* Select All row */}
                    <div style={{ padding: "8px 14px", borderBottom: "2px solid #e2e8f0", background: "#f8fafc", display: "flex", alignItems: "center", gap: 10, position: "sticky", top: 0 }}>
                      <input
                        type="checkbox"
                        id="select-all-members"
                        checked={selectedMemberIds.length === availableMembers.filter(m =>
                          !memberSearch || m.name.toLowerCase().includes(memberSearch.toLowerCase()) || (m.emp_id||"").toLowerCase().includes(memberSearch.toLowerCase())
                        ).length && availableMembers.filter(m =>
                          !memberSearch || m.name.toLowerCase().includes(memberSearch.toLowerCase()) || (m.emp_id||"").toLowerCase().includes(memberSearch.toLowerCase())
                        ).length > 0}
                        onChange={e => {
                          const visible = availableMembers.filter(m =>
                            !memberSearch || m.name.toLowerCase().includes(memberSearch.toLowerCase()) || (m.emp_id||"").toLowerCase().includes(memberSearch.toLowerCase())
                          );
                          if (e.target.checked) {
                            const newIds = [...new Set([...selectedMemberIds, ...visible.map(m => m.id)])];
                            setSelectedMemberIds(newIds);
                          } else {
                            const visibleIds = new Set(visible.map(m => m.id));
                            setSelectedMemberIds(selectedMemberIds.filter(id => !visibleIds.has(id)));
                          }
                        }}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                      <label htmlFor="select-all-members" style={{ fontSize: 12, fontWeight: 700, color: "#374151", cursor: "pointer", flex: 1 }}>
                        Select All {memberSearch ? "Filtered" : ""} ({availableMembers.filter(m =>
                          !memberSearch || m.name.toLowerCase().includes(memberSearch.toLowerCase()) || (m.emp_id||"").toLowerCase().includes(memberSearch.toLowerCase())
                        ).length})
                      </label>
                      {selectedMemberIds.length > 0 && (
                        <span style={{ fontSize: 11, background: "#dbeafe", color: "#1d4ed8", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>
                          {selectedMemberIds.length} selected
                        </span>
                      )}
                    </div>

                    {/* Individual member rows — compact, no expanding inputs */}
                    {availableMembers
                      .filter(m => !memberSearch || m.name.toLowerCase().includes(memberSearch.toLowerCase()) || (m.emp_id||"").toLowerCase().includes(memberSearch.toLowerCase()))
                      .map(m => {
                        const isChecked = selectedMemberIds.includes(m.id);
                        return (
                          <label key={m.id}
                            htmlFor={`member-chk-${m.id}`}
                            style={{ padding: "9px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10, background: isChecked ? "#eff6ff" : "#fff", transition: "background 0.12s", cursor: "pointer", userSelect: "none" }}>
                            <input
                              id={`member-chk-${m.id}`}
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                setSelectedMemberIds(prev =>
                                  prev.includes(m.id) ? prev.filter(id => id !== m.id) : [...prev, m.id]
                                );
                              }}
                              style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0, accentColor: "#4f46e5" }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{m.name}</span>
                                <span style={{ fontSize: 11, color: "#94a3b8" }}>({m.emp_id})</span>
                                <span style={{ fontSize: 10, background: isChecked ? "#dbeafe" : "#f1f5f9", color: isChecked ? "#1d4ed8" : "#64748b", padding: "1px 7px", borderRadius: 10, fontWeight: 600 }}>
                                  {m.role || m.designation || "Employee"}
                                </span>
                              </div>
                            </div>
                            {isChecked && (
                              <span style={{ fontSize: 10, color: "#4f46e5", fontWeight: 700, whiteSpace: "nowrap" }}>✔ Selected</span>
                            )}
                          </label>
                        );
                      })}
                  </>
                )}
              </div>
            )}

            {/* Selected members — assign role per person here */}
            {selectedMemberIds.length > 0 && (
              <div style={{ border: "1px solid #c7d2fe", borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
                <div style={{ background: "#eef2ff", padding: "8px 14px", fontSize: 12, fontWeight: 700, color: "#4338ca", display: "flex", alignItems: "center", gap: 6 }}>
                  ✔ {selectedMemberIds.length} selected — assign project role below
                </div>
                {selectedMemberIds.map((uid, idx) => {
                  const m = availableMembers.find(x => x.id === uid);
                  if (!m) return null;
                  return (
                    <div key={uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", borderTop: idx > 0 ? "1px solid #e0e7ff" : "none", background: "#fff" }}>
                      {/* Member name */}
                      <div style={{ minWidth: 110, flexShrink: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{m.name}</div>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>{m.emp_id}</div>
                      </div>
                      {/* Role input */}
                      <input
                        value={memberRoles[uid] || ""}
                        placeholder="Role in project (e.g. QA, Frontend, Lead)"
                        onChange={e => setMemberRoles(prev => ({ ...prev, [uid]: e.target.value }))}
                        style={{ flex: 1, padding: "6px 10px", fontSize: 12, borderRadius: 7, border: "1.5px solid #a5b4fc", outline: "none", background: "#fafafe" }}
                      />
                      {/* Remove */}
                      <button
                        onClick={() => setSelectedMemberIds(prev => prev.filter(id => id !== uid))}
                        style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16, padding: "0 4px", lineHeight: 1, flexShrink: 0 }}
                        title="Remove">✕</button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pmprojectspg-inline-51">
              <span className="pmprojectspg-inline-52">Note:</span> Selected members will be added to the project and project chat group.
            </div>

            <div className="pmprojectspg-inline-53">
              <button className="btn-o" onClick={() => {
                setShowMemberModal(false);
                setSelectedProject(null);
                resetMemberForm();
              }}>Cancel</button>
              <button
                className="btn"
                onClick={handleAddMember}
                disabled={selectedMemberIds.length === 0}>
                {II("plus", 14)} Add {selectedMemberIds.length > 0 ? `${selectedMemberIds.length} Member${selectedMemberIds.length > 1 ? "s" : ""}` : "Members"}
              </button>
            </div>
          </div>
        )}
      </PMModal>

            {/* ========== UPDATE PROGRESS MODAL ========== */}
      <PMModal
        open={showProgressModal}
        onClose={() => {
          setShowProgressModal(false);
          setSelectedMember(null);
        }}
        title={`Update Progress — ${selectedMember?.name || ''}`}>
        
        {selectedMember &&
        <div>
            <div className="pmprojectspg-inline-54">





            
              <div className="pmprojectspg-inline-55">{selectedMember.name}</div>
              <div className="pmprojectspg-inline-56">
                {selectedMember.emp_id || selectedMember.id} · {selectedMember.role || selectedMember.project_role}
              </div>
            </div>
            
            <div className="fg">
              <label className="fl">Progress (%)</label>
              <input
              className="fi"
              type="range"
              min="0"
              max="100"
              value={progressForm.progress}
              onChange={(e) => setProgressForm({ ...progressForm, progress: parseInt(e.target.value) })} />
            
              <div className="pmprojectspg-inline-57">
                {progressForm.progress}%
              </div>
            </div>
            
            <div className="fg">
              <label className="fl">Status</label>
              <select
              className="fsel"
              value={progressForm.status}
              onChange={(e) => setProgressForm({ ...progressForm, status: e.target.value })}>
              
                <option value="On Track">On Track</option>
                <option value="Needs Attention">Needs Attention</option>
                <option value="Behind">Behind</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            
            <div className="pmprojectspg-inline-58">
              <button className="btn-o" onClick={() => {
              setShowProgressModal(false);
              setSelectedMember(null);
            }}>Cancel</button>
              <button className="btn" onClick={handleUpdateProgress}>
                {II("chk", 14)} Update Progress
              </button>
            </div>
          </div>
        }
      </PMModal>

      {/* ========== DELETE PROJECT MODAL ========== */}
      <PMModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedProject(null);
        }}
        title="Delete Project">
        
        {selectedProject &&
        <div>
            <div className="pmprojectspg-inline-59">





            
              <div className="pmprojectspg-inline-60">
                <div className="pmprojectspg-inline-61">








                
                  {II("trash", 20)}
                </div>
                <div>
                  <div className="pmprojectspg-inline-62">{selectedProject.name}</div>
                  <div className="pmprojectspg-inline-63">{selectedProject.pid}</div>
                </div>
              </div>
              
              <div className="pmprojectspg-inline-64">






              
                ⚠️ This action cannot be undone. This will permanently delete:
                <ul className="pmprojectspg-inline-65">
                  <li>The project and all its data</li>
                  <li>All team members will be removed</li>
                  <li>All tasks and documents will be deleted</li>
                  <li>The project chat group will be deleted</li>
                </ul>
              </div>
            </div>
            
            <div className="pmprojectspg-inline-66">
              <button className="btn-o" onClick={() => {
              setShowDeleteModal(false);
              setSelectedProject(null);
            }}>Cancel</button>
              <button
              className="btn pmprojectspg-inline-67"

              onClick={handleDeleteProject}>
              
                {II("trash", 14)} Delete Project
              </button>
            </div>
          </div>
        }
      </PMModal>
    </div>);

}

export default PMProjectsPg;